import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { X, Printer, FileText } from "lucide-react";
import LabOrderService from "../../../../Services/LabOrderService";

const dateOnly = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : value;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const pad = (part) => String(part).padStart(2, "0");
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
      value.getDate()
    )}`;
  }
  return String(value);
};

export default function DischargePreview({
  isOpen,
  onClose,
  patientDetails,
  admission,
  admissionId,
  patientId,
  data: propData,
}) {
  const reduxDischarge = useSelector((state) => state.discharge || {});
  const [labReports, setLabReports] = useState([]);
  const [loadingLab, setLoadingLab] = useState(false);

  // Read doctor info from session storage
  const loginData = sessionStorage.getItem("login_result");
  const loginDataObject = loginData ? JSON.parse(loginData) : null;
  const sessionDoctorName =
    loginDataObject?.user?.userName || loginDataObject?.user?.firstName
      ? `Dr. ${loginDataObject?.user?.firstName || ""} ${
          loginDataObject?.user?.lastName || ""
        }`.trim()
      : "Doctor";
  const sessionDoctorSpeciality =
    loginDataObject?.user?.speciality ||
    patientDetails?.departmentName ||
    "General Medicine";
  const clinicName =
    loginDataObject?.clinic?.clinicName ||
    patientDetails?.clinicName ||
    "Cytozon Health Care";

  const effectivePatientId =
    patientId || patientDetails?.patientId || reduxDischarge?.patientId || "";

  // Fetch lab reports if not passed in
  useEffect(() => {
    if (isOpen && effectivePatientId) {
      if (propData?.labReports && propData.labReports.length > 0) {
        setLabReports(propData.labReports);
        return;
      }
      setLoadingLab(true);
      const payload = {
        patientId: effectivePatientId,
        reportUploaded: "Y",
        status: 1,
        patientClass: "IPD",
      };
      LabOrderService.LabReportUploadGet(payload)
        .then((res) => {
          const list = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
          setLabReports(list);
        })
        .catch((err) => console.error("Lab reports preview error:", err))
        .finally(() => setLoadingLab(false));
    }
  }, [isOpen, effectivePatientId, propData]);

  if (!isOpen) return null;

  // Merge live Redux discharge state and propData
  const dischargeState = {
    ...reduxDischarge,
    ...(propData || {}),
  };

  // Demographics
  const patient = patientDetails || propData?.patient || {};
  const patientName =
    patient?.patientName ||
    [patient?.salutation, patient?.firstName, patient?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    dischargeState?.patientName ||
    "—";

  const patId = patient?.patientId || effectivePatientId || "—";
  const ipNo =
    admission?.admissionNumber ||
    admission?.ipNumber ||
    admission?.admissionId ||
    admissionId ||
    patient?.admissionNo ||
    dischargeState?.encounterId ||
    "—";

  const rawAddressParts = [
    patient?.address || patient?.address1,
    patient?.address2,
    patient?.city || patient?.district,
    patient?.state,
  ].filter(Boolean);
  const addressText =
    rawAddressParts.length > 0
      ? rawAddressParts.join(", ")
      : patient?.address || "—";

  const phoneNo =
    patient?.mobile ||
    patient?.phone ||
    patient?.contactNumber ||
    "—";

  const ageStr = patient?.age ? `${patient.age} Yrs` : patient?.ageDob || "—";
  const genderStr =
    patient?.gender === "M" || patient?.gender === "Male"
      ? "Male"
      : patient?.gender === "F" || patient?.gender === "Female"
      ? "Female"
      : patient?.gender || "—";
  const ageAndSex =
    [ageStr !== "—" ? ageStr : null, genderStr !== "—" ? genderStr : null]
      .filter(Boolean)
      .join(", ") || "—";

  const admittedOn =
    dateOnly(patient?.admissionDate || admission?.admissionDate) || "—";
  const dischargedOn = dateOnly(new Date());

  const doctorNameDisplay =
    dischargeState?.authDocName ||
    sessionDoctorName ||
    patient?.admissionDoctorName ||
    "—";
  const departmentDisplay =
    sessionDoctorSpeciality || patient?.departmentName || "—";

  // Clinical data lists
  const diagnosisList = (dischargeState?.dischargeDiagnosis || []).filter(
    (d) => d.status !== 0 && d.activeFlag !== "N"
  );
  const primaryDiagnoses = diagnosisList.filter(
    (item) =>
      item.diagnosisType &&
      item.diagnosisType.trim().toLowerCase() === "primary"
  );
  const secondaryDiagnoses = diagnosisList.filter(
    (item) =>
      item.diagnosisType &&
      item.diagnosisType.trim().toLowerCase() === "secondary"
  );
  const otherDiagnoses = diagnosisList.filter(
    (item) =>
      !item.diagnosisType ||
      (item.diagnosisType.trim().toLowerCase() !== "primary" &&
        item.diagnosisType.trim().toLowerCase() !== "secondary")
  );

  const activeAllergies = (dischargeState?.dischargeAllergy || []).filter(
    (a) => a.status !== 0 && (a.allergyName || a.reaction)
  );

  const treatments = dischargeState?.dischargeTreatmentSummary || {};
  const treatmentEntries = [
    ["Surgery Details", treatments.surgeryDetails],
    ["Procedures", treatments.procedureDesc || treatments.procedures],
    ["IV Fluids", treatments.ivFluidDesc || treatments.ivFluids],
    ["Oxygen Therapy", treatments.oxygenTherapyDesc || treatments.oxygenTherapy],
    ["Physiotherapy", treatments.physiotherapyDesc || treatments.physiotherapy],
    ["Diet Plan", treatments.dietPlanDesc || treatments.dietPlan],
    ["Comments", treatments.comments],
  ].filter(([, val]) => Boolean(val && String(val).trim()));

  const visibleMedications = (dischargeState?.dischargeMedication || []).filter(
    (item) => item.status !== 0 && (item.drugName || item.name)
  );

  const dietList = (dischargeState?.dischargeDietDtls || [])
    .map((d) => d.dietName || d.name)
    .filter(Boolean);

  const instructionsList = (dischargeState?.dischargePatientInstruction || [])
    .map((ins) => ins.instrName || ins.name)
    .filter(Boolean);

  const carePlanList = (dischargeState?.dischargeCarePlanDtls || [])
    .map((cp) => cp.carePlanName || cp.name)
    .filter(Boolean);

  const dispositionList = (
    Array.isArray(dischargeState?.dischargeDispositionDtls) &&
    dischargeState.dischargeDispositionDtls.length > 0
      ? dischargeState.dischargeDispositionDtls.map(
          (dp) => dp.dispositionName || dp.name
        )
      : dischargeState?.dispositionDesc
      ? [dischargeState.dispositionDesc]
      : []
  ).filter(Boolean);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 overflow-y-auto"
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        className="bg-white rounded-xl w-full max-w-4xl max-h-[94vh] flex flex-col shadow-2xl overflow-hidden border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Control Bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 print:hidden">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#800020]" />
            <span className="text-sm font-extrabold text-[#800020] tracking-wider uppercase">
              Discharge Summary Preview
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-1.5 bg-gray-50 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition cursor-pointer"
              title="Close Preview"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Document Scroll Body */}
        <div className="overflow-y-auto p-4 sm:p-6 flex justify-center bg-gray-50 print:p-0 print:bg-white print:overflow-visible">
          {/* Printable Sheet Paper */}
          <div
            id="discharge-summary-printable"
            className="w-full bg-white p-5 sm:p-8 rounded shadow-md border border-gray-300 font-sans text-black leading-relaxed print:shadow-none print:border-none print:p-0"
          >
            {/* Optional Clinic Branding Header */}
            {clinicName && (
              <div className="flex items-center justify-between pb-3 mb-2 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-extrabold text-gray-900 tracking-tight">
                    {clinicName}
                  </h2>
                  <p className="text-xs text-gray-500">In-Patient Care Services</p>
                </div>
                <div className="text-right">
                  <span className="text-[11px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 px-2.5 py-1 rounded">
                    Official Medical Document
                  </span>
                </div>
              </div>
            )}

            {/* Title Section (Maroon Centered Title from Template) */}
            <div className="text-center py-2 mb-3">
              <h1 className="text-base sm:text-lg font-black text-[#800020] tracking-widest uppercase">
                DISCHARGE SUMMARY
              </h1>
            </div>

            {/* Demographics Bordered Table Grid */}
            <div className="border-[1.5px] border-black text-xs mb-0">
              {/* Row 1 */}
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 sm:border-r-[1.5px] border-black border-b sm:border-b-0">
                  <span className="font-bold">Patient Name. : </span>
                  <span className="font-medium">{patientName}</span>
                </div>
                <div className="p-2">
                  <span className="font-bold">Patient Id. : </span>
                  <span className="font-medium">{patId}</span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 border-t-[1.5px] border-black">
                <div className="p-2 sm:border-r-[1.5px] border-black border-b sm:border-b-0 space-y-1">
                  <div>
                    <span className="font-bold">Address : </span>
                    <span className="font-medium">{addressText}</span>
                  </div>
                  <div>
                    <span className="font-bold">Mobile No: </span>
                    <span className="font-medium">{phoneNo}</span>
                  </div>
                </div>

                <div className="p-2 space-y-1">
                  <div>
                    <span className="font-bold">IPD No : </span>
                    <span className="font-medium">{ipNo}</span>
                  </div>
                  <div>
                    <span className="font-bold">Age & Gender : </span>
                    <span className="font-medium">{ageAndSex}</span>
                  </div>
                  <div>
                    <span className="font-bold">Admitted On : </span>
                    <span className="font-medium">{admittedOn}</span>
                  </div>
                  <div>
                    <span className="font-bold">Discharged On : </span>
                    <span className="font-medium">{dischargedOn}</span>
                  </div>
                </div>
              </div>

              {/* Row 3 (Attending Doctor & Speciality) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 border-t-[1.5px] border-black">
                <div className="p-2 sm:border-r-[1.5px] border-black border-b sm:border-b-0">
                  <span className="font-bold">Attending Doctor: </span>
                  <span className="font-medium">{doctorNameDisplay}</span>
                </div>
                <div className="p-2">
                  <span className="font-bold">Department / Speciality: </span>
                  <span className="font-medium">{departmentDisplay}</span>
                </div>
              </div>
            </div>

            {/* 1. Diagnosis */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                1. Diagnosis :
              </h3>
              {diagnosisList.length > 0 ? (
                <div className="pl-3 mt-1.5 space-y-2 text-xs">
                  {primaryDiagnoses.length > 0 && (
                    <div>
                      <p className="font-bold text-black mb-1">
                        Primary Diagnosis:
                      </p>
                      {primaryDiagnoses.map((item, idx) => (
                        <div key={idx} className="flex gap-2 mb-1 pl-2">
                          <span>{idx + 1}.</span>
                          <div>
                            <span className="text-black">
                              {item.diagnosisDesc || item.diagnosisDescription || "No Description"}{" "}
                              {item.diagnosisCode && (
                                <span className="font-bold">
                                  ({item.diagnosisCode})
                                </span>
                              )}
                            </span>
                            {item.remarks && (
                              <p className="text-[11px] text-slate-600 italic mt-0.5">
                                <span className="font-bold">Remarks: </span>
                                {item.remarks}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {secondaryDiagnoses.length > 0 && (
                    <div>
                      <p className="font-bold text-black mb-1">
                        Secondary Diagnosis:
                      </p>
                      {secondaryDiagnoses.map((item, idx) => (
                        <div key={idx} className="flex gap-2 mb-1 pl-2">
                          <span>{idx + 1}.</span>
                          <div>
                            <span className="text-black">
                              {item.diagnosisDesc || item.diagnosisDescription || "No Description"}{" "}
                              {item.diagnosisCode && (
                                <span className="font-bold">
                                  ({item.diagnosisCode})
                                </span>
                              )}
                            </span>
                            {item.remarks && (
                              <p className="text-[11px] text-slate-600 italic mt-0.5">
                                <span className="font-bold">Remarks: </span>
                                {item.remarks}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {otherDiagnoses.length > 0 && (
                    <div>
                      <p className="font-bold text-black mb-1">
                        Other Diagnoses:
                      </p>
                      {otherDiagnoses.map((item, idx) => (
                        <div key={idx} className="flex gap-2 mb-1 pl-2">
                          <span>{idx + 1}.</span>
                          <div>
                            <span className="text-black">
                              {item.diagnosisDesc || item.diagnosisDescription || "Diagnosis"}{" "}
                              {item.diagnosisCode && (
                                <span className="font-bold">
                                  ({item.diagnosisCode})
                                </span>
                              )}
                            </span>
                            {item.remarks && (
                              <p className="text-[11px] text-slate-600 italic mt-0.5">
                                <span className="font-bold">Remarks: </span>
                                {item.remarks}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic pl-3 mt-1">
                  No diagnosis entered.
                </p>
              )}
            </div>

            {/* 2. Reason for Admission (Main Complaint) */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                2. Reason for Admission (Main Complaint) :
              </h3>
              <p className="text-xs italic pl-3 mt-1 text-black">
                {dischargeState?.chiefComplaint || "Not entered"}
              </p>
            </div>

            {/* 3. Clinical Summary / History of Illness */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                3. History of Present Illness / Clinical Summary :
              </h3>
              <p className="text-xs italic pl-3 mt-1 text-black">
                {dischargeState?.clinicalSummary || "No clinical summary entered."}
              </p>
            </div>

            {/* 4. Allergies */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                4. Allergies :
              </h3>
              {activeAllergies.length > 0 ? (
                <div className="pl-3 mt-1 space-y-1 text-xs">
                  {activeAllergies.map((item, idx) => (
                    <p key={idx} className="font-semibold text-red-800">
                      • {item.allergyName || "Allergy"}{" "}
                      {item.severity ? `(${item.severity})` : ""}
                      {item.reaction ? ` - Reaction: ${item.reaction}` : ""}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic pl-3 mt-1">
                  No known allergies / medical illnesses recorded.
                </p>
              )}
            </div>

            {/* 5. Procedures / Surgeries & Treatments */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                5. Procedures / Surgeries & Treatment Summary :
              </h3>
              {treatmentEntries.length > 0 ? (
                <div className="pl-3 mt-1 space-y-1 text-xs">
                  {treatmentEntries.map(([label, val], idx) => (
                    <div key={idx} className="flex gap-2">
                      <span className="font-bold min-w-[130px]">{label}:</span>
                      <span className="italic">{String(val)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic pl-3 mt-1">
                  No treatment details entered.
                </p>
              )}
            </div>

            {/* 6. Investigation Summary (Lab Reports) */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                6. Investigation Summary :
              </h3>
              {loadingLab ? (
                <p className="text-xs text-slate-400 italic pl-3 mt-1">
                  Loading investigation reports...
                </p>
              ) : labReports.length > 0 ? (
                <div className="pl-3 mt-1 space-y-1 text-xs">
                  {labReports.map((item, idx) => (
                    <p key={idx} className="font-medium text-black">
                      • {item.labTestName || "Lab Test Report"}{" "}
                      <span className="text-slate-500 font-normal">
                        - {dateOnly(item.orderDate || item.uploadedDateTime || item.createdDate)}
                        {item.diagnosticType ? ` (${item.diagnosticType})` : ""}
                        {item.statusDesc || item.status ? ` • ${item.statusDesc || item.status}` : ""}
                      </span>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic pl-3 mt-1">
                  No lab reports available.
                </p>
              )}
            </div>

            {/* 7. Condition at Discharge */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                7. Condition at Discharge :
              </h3>
              <div className="pl-3 mt-1 text-xs">
                <span className="font-semibold italic">
                  {dischargeState?.conditionDesc || "Stable"}
                </span>
                {dischargeState?.conditionRemarks && (
                  <p className="text-slate-600 italic mt-0.5">
                    Remarks: {dischargeState.conditionRemarks}
                  </p>
                )}
              </div>
            </div>

            {/* 8. Advice / Medications on Discharge */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                8. Advice / Medications on Discharge :
              </h3>
              {visibleMedications.length > 0 ? (
                <div className="border border-black mt-2 text-xs">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 bg-slate-100 font-bold border-b border-black py-1.5 px-2">
                    <div className="col-span-4 border-r border-black pr-2">
                      Medicine Name
                    </div>
                    <div className="col-span-6 border-r border-black px-2">
                      Dosage & Details
                    </div>
                    <div className="col-span-2 text-center pl-2">Duration</div>
                  </div>

                  {/* Table Rows */}
                  {visibleMedications.map((item, idx) => {
                    const drugName =
                      item.drugName || item.name || "Medication";
                    const strength = item.strength || "";
                    const dosage = item.dosage || "";
                    const frequency = item.frequency || "";
                    const route = item.route || "";
                    const instruction = item.instruction || "";
                    const durationStr = item.duration || "—";
                    const startDate = dateOnly(item.startDate);
                    const endDate = dateOnly(item.endDate);
                    const dateRange =
                      startDate || endDate
                        ? [startDate, endDate].filter(Boolean).join(" → ")
                        : "";

                    const details = [
                      strength ? { label: "Strength", value: strength } : null,
                      dosage ? { label: "Dosage", value: dosage } : null,
                      frequency ? { label: "Frequency", value: frequency } : null,
                      route ? { label: "Route", value: route } : null,
                      instruction ? { label: "Instruction", value: instruction } : null,
                      dateRange ? { label: "Date", value: dateRange } : null,
                    ].filter(Boolean);

                    return (
                      <div
                        key={idx}
                        className={`grid grid-cols-12 py-1.5 px-2 ${
                          idx > 0 ? "border-t border-black" : ""
                        }`}
                      >
                        {/* Medicine Name */}
                        <div className="col-span-4 border-r border-black pr-2">
                          <p className="font-bold text-black">
                            {idx + 1}. {drugName}
                          </p>
                        </div>

                        {/* Dosage & Details */}
                        <div className="col-span-6 border-r border-black px-2 space-y-0.5">
                          {details.length > 0 ? (
                            details.map((d, dIdx) => (
                              <p key={dIdx} className="text-[11px] leading-tight">
                                <span className="font-bold text-slate-700">
                                  {d.label}:{" "}
                                </span>
                                <span>{d.value}</span>
                              </p>
                            ))
                          ) : (
                            <span className="italic text-slate-500">
                              As directed
                            </span>
                          )}
                        </div>

                        {/* Duration */}
                        <div className="col-span-2 text-center pl-2 font-semibold">
                          {durationStr}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic pl-3 mt-1">
                  No medication entered.
                </p>
              )}
            </div>

            {/* 9. Diet */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                9. Diet Advice :
              </h3>
              <p className="text-xs italic pl-3 mt-1 text-black">
                {dietList.length > 0
                  ? dietList.join(" • ")
                  : "Normal diet / as advised."}
              </p>
            </div>

            {/* 10. Date of next appointments & followup instructions */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                10. Date of next appointments & followup instructions :
              </h3>
              <div className="pl-3 mt-1 text-xs space-y-1">
                {dischargeState?.followupDate && (
                  <p className="italic">
                    <span className="font-bold not-italic">
                      Follow-up Date:{" "}
                    </span>
                    {dateOnly(dischargeState.followupDate)}
                    {dischargeState?.followupDoctorName && (
                      <span> with {dischargeState.followupDoctorName}</span>
                    )}
                    {dischargeState?.followupDepartmentDesc && (
                      <span> ({dischargeState.followupDepartmentDesc})</span>
                    )}
                  </p>
                )}
                {dischargeState?.followupInstruction && (
                  <p className="italic">
                    <span className="font-bold not-italic">Notes: </span>
                    {dischargeState.followupInstruction}
                  </p>
                )}
                {instructionsList.length > 0 && (
                  <div className="mt-1">
                    <span className="font-bold not-italic">
                      Patient Instructions:
                    </span>
                    {instructionsList.map((inst, idx) => (
                      <p key={idx} className="italic pl-2">
                        • {inst}
                      </p>
                    ))}
                  </div>
                )}
                {dischargeState?.additionalInstruction && (
                  <p className="italic pl-2">
                    • {dischargeState.additionalInstruction}
                  </p>
                )}
                {!dischargeState?.followupDate &&
                  !dischargeState?.followupInstruction &&
                  instructionsList.length === 0 &&
                  !dischargeState?.additionalInstruction && (
                    <p className="text-slate-500 italic">
                      No specific follow-up instructions entered.
                    </p>
                  )}
              </div>
            </div>

            {/* 11. To Obtain Urgent Care (When) & Care Plan */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                11. To Obtain Urgent Care (When) & Care Plan :
              </h3>
              <div className="pl-3 mt-1 text-xs space-y-1">
                {carePlanList.length > 0 && (
                  <div>
                    <span className="font-bold">Care Plan:</span>
                    {carePlanList.map((cp, idx) => (
                      <p key={idx} className="italic pl-2">
                        • {cp}
                      </p>
                    ))}
                  </div>
                )}
                <p className="italic text-slate-700">
                  Seek urgent medical consultation in case of acute pain,
                  swelling, discharge, severe fever, or worsening symptoms.
                </p>
              </div>
            </div>

            {/* 12. Disposition */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                12. Disposition :
              </h3>
              <div className="pl-3 mt-1 text-xs space-y-1">
                <p className="font-semibold text-black">
                  {dispositionList.length > 0
                    ? dispositionList.join(" • ")
                    : "Discharged Home"}
                </p>
                {(dischargeState?.additionalDispositionNotes ||
                  dischargeState?.dispositionNotes) && (
                  <p className="italic text-slate-600">
                    <span className="font-bold not-italic">Notes: </span>
                    {dischargeState.additionalDispositionNotes ||
                      dischargeState.dispositionNotes}
                  </p>
                )}
              </div>
            </div>

            {/* 13. Doctor Remarks */}
            <div className="border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-black p-2.5">
              <h3 className="text-xs sm:text-sm font-bold text-[#1E3A8A]">
                13. Doctor Remarks :
              </h3>
              <p className="text-xs italic pl-3 mt-1 text-black">
                {dischargeState?.doctorRemark || "No additional remarks."}
              </p>
            </div>

            {/* 14. Signatures Table */}
            <div className="border-[1.5px] border-black text-xs">
              {/* Header Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 border-b-[1.5px] border-black">
                <div className="p-2 sm:border-r-[1.5px] border-black border-b sm:border-b-0">
                  <span className="font-bold">Prepared / Verified By: </span>
                  <span>{doctorNameDisplay}</span>
                </div>
                <div className="p-2">
                  <span className="font-bold">Attending Physician: </span>
                  <span>{doctorNameDisplay}</span>
                </div>
              </div>

              {/* Signature Graphics Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-3 sm:border-r-[1.5px] border-black border-b sm:border-b-0 flex flex-col items-center justify-center">
                  <div className="h-16 flex items-center justify-center text-slate-400 italic">
                    Hospital Stamp on Record
                  </div>
                  <div className="w-3/5 border-t border-slate-500 pt-1 text-center font-semibold text-[11px]">
                    Stamp
                  </div>
                </div>

                <div className="p-3 flex flex-col items-center justify-center">
                  <div
                    className="h-16 flex items-center justify-center text-lg italic text-slate-700"
                    style={{ fontFamily: "cursive" }}
                  >
                    {doctorNameDisplay.replace("Dr. ", "") || "Doctor Signature"}
                  </div>
                  <div className="w-3/5 border-t border-slate-500 pt-1 text-center font-semibold text-[11px]">
                    Signature
                  </div>
                </div>
              </div>
            </div>

            {/* Document Page Footer */}
            <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-200 text-[11px] text-gray-500">
              <span>Discharge Summary Report</span>
              <span>Patient Counseled: {dischargeState?.patientCounseled === "Y" ? "Yes" : "No"}</span>
              <span>Generated on: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
